import type { ForecastQueueRow } from '@/types/backofficeAnalytics.types'

/**
 * The Case Design stages, in pipeline order — the pill row under the rollups
 * (sc-1212).
 *
 * ## The two tiers, and which answers what
 *
 * Tyler, 2026-10-20, having seen the first attempt render as plain pills:
 * *"past due and the others look like pills, not rollup colored cards. Then i
 * imagined below those to show the pills style and justify centered based on
 * status. and i envisioned them to always show."*
 *
 * - **Rollup cards** (`forecast-status-pills.ts`) — four grouped counts, Past
 *   due / Need started / Open / Ready. The summary.
 * - **These pills** — the stages those groups are made of, so a reader can see
 *   WHERE the work actually sits. On production 2026-10-20, "Need started 56"
 *   is 16 cases whose client has not finished data gathering and **41 sitting
 *   in RC Data Entry**, which is the biggest single pile of real work in the
 *   shop. The card alone hides that; the pills are the point.
 *
 * ## Why the labels are hardcoded when the row already carries one
 *
 * `ForecastQueueRow.stageLabel` is the server's full name — "Alternative
 * Scenarios Needed" — which does not fit a pill beside a count. The repo
 * already hit this and solved it the same way: `REWORK_STAGE_LABELS` in
 * `forecast-queue-filters.ts` hardcoded two of these ids with short labels for
 * the rework filter's chips, and **this module is that map widened to all
 * seven, so there is one stage vocabulary rather than two.** That filter and
 * its two constants are now deleted: Revisions and Alt scenarios are pills of
 * their own here, which made the dropdown a second control for the same cut.
 *
 * Hardcoding the ids is safe for the reason that map already records: they are
 * identical in both HubSpot portals (production 45025262, sandbox 51387696),
 * and the API module that owns them is not reachable from `apps/web`.
 *
 * ## Order
 *
 * Pipeline order, not the order the API happens to list
 * `OPEN_CASE_DESIGN_STAGE_IDS` in. A reader scans this row to find where cases
 * pile up, so it has to read along the path a case actually takes: waiting on
 * the client, then data entry, then planning, then the two ways work comes
 * back.
 */

export interface CaseDesignStage {
  id: string
  /**
   * The stage's name in the page's URL (`?stage=data-entry,planning`), so a
   * pasted link tells its reader what it shows. **A URL vocabulary only: the
   * id stays the wire and HubSpot identity** and is what the page's state and
   * every predicate use; the slug is mapped to and from it at the search-param
   * boundary. The ids are already hardcoded here as identical across the
   * production and sandbox portals, so the slug map adds no new coupling.
   * Slugs must never be JSON-parseable (no bare numbers): see
   * `forecasts-search-schema.ts`.
   */
  slug: string
  /** Pill text, short enough to sit beside a count at phone width. */
  label: string
  /** The server's own name, for a tooltip and the Reference tab. */
  fullLabel: string
}

export const CASE_DESIGN_STAGES: ReadonlyArray<CaseDesignStage> = [
  {
    id: '1333293094',
    slug: 'dg-incomplete',
    label: 'DG incomplete',
    fullLabel: 'DG Incomplete',
  },
  {
    id: '1058382868',
    slug: 'data-entry',
    label: 'Data entry',
    fullLabel: 'RC Data Entry',
  },
  {
    id: '1058382869',
    slug: 'entry-done',
    label: 'Entry done',
    fullLabel: 'Data Entry Complete',
  },
  {
    id: '1058382870',
    slug: 'planning',
    label: 'Planning',
    fullLabel: 'Planning in Progress',
  },
  {
    id: '1058207543',
    slug: 'needs-info',
    label: 'Needs info',
    fullLabel: 'Additional Information Needed',
  },
  {
    id: '1123638137',
    slug: 'revisions',
    label: 'Revisions',
    fullLabel: 'Revisions Needed',
  },
  {
    id: '1329726795',
    slug: 'alt-scenarios',
    label: 'Alt scenarios',
    fullLabel: 'Alternative Scenarios Needed',
  },
]

/**
 * The two stages a case only reaches because someone asked for a change after
 * the plan was delivered — the rework filter's options, derived here rather
 * than listed again.
 *
 * Kept after the rework dropdown was deleted, because the two stages are a
 * named pair in the domain and the next reader needing them should find them
 * derived rather than relist the ids. **Additional Information Needed is deliberately
 * NOT one of them**, mirroring the API's `FIRST_PASS_PLAN_WORK_STAGE_IDS`: that
 * is the specialist blocked waiting on the client for missing data on the way
 * to a FIRST delivery, not a change request on one already delivered.
 */
export const REWORK_STAGES: ReadonlyArray<CaseDesignStage> =
  CASE_DESIGN_STAGES.filter((stage) =>
    ['1123638137', '1329726795'].includes(stage.id),
  )

/**
 * How many open rows sit in each stage.
 *
 * **Every stage is present, zeros included.** A pill reading 0 says "nothing
 * is here", which is information a reader scanning for a pile-up needs; a
 * missing pill reads as a missing feature. Same rule `groupCounts` and
 * `statusPillCounts` follow.
 *
 * Counted from rows that passed every OTHER filter but not the stage
 * selection, so a pill reads the same selected or not.
 */
export function stagePillCounts(
  rows: ReadonlyArray<Pick<ForecastQueueRow, 'stageId'>>,
): Record<string, number> {
  const counts = Object.fromEntries(
    CASE_DESIGN_STAGES.map((stage) => [stage.id, 0]),
  )
  for (const row of rows) {
    if (row.stageId in counts) counts[row.stageId] += 1
  }
  return counts
}

/**
 * Open rows in the selected stages. Empty selection = every row, the same
 * permissive convention as every other multi-select on this page.
 */
export function narrowRowsByStage<
  TRow extends Pick<ForecastQueueRow, 'stageId'>,
>(rows: ReadonlyArray<TRow>, selected: ReadonlyArray<string>): Array<TRow> {
  if (selected.length === 0) return [...rows]
  return rows.filter((row) => selected.includes(row.stageId))
}

/**
 * Is a stage selection capable of narrowing this list?
 *
 * False for the two lists whose rows carry no Case Design stage — Billable
 * work spans every stage including Closed and is anchored on meetings rather
 * than a stage, and Needs Cleaned is a record-quality list paged by the server.
 *
 * **The pills still RENDER on those tabs** (Tyler: *"i envisioned them to
 * always show"*), because their counts are a standing summary of the open book
 * and do not depend on which tab is open. They render inert, saying so, rather
 * than silently doing nothing on a click — which is the honest half of the rule
 * that a control must not filter where it is invisible.
 */
export function stagePillsNarrow(scope: string): boolean {
  return scope !== 'postDelivery' && scope !== 'cleaning'
}
